<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage, Checkbox, Tooltip, InfoTooltip, SearchNoResults } from '@/shared/ui';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import type { AdminMember } from '../../model';
  import {
    departmentOverflowFromMember,
    positionNamesOverflowFromMember,
    functionalActivitiesOverflowFromMember,
  } from '../../lib/member-overflow-display';
  import { formatMemberAuditDateTime } from '@/pages/admin/lib';
  import MemberOverflowCell from './MemberOverflowCell.vue';

  const props = withDefaults(
    defineProps<{
      members: AdminMember[];
      isSelected: (id: number) => boolean;
      allSelected: boolean;
      searchTerm?: string;
      isFilterActive?: boolean;
      loading?: boolean;
    }>(),
    {
      searchTerm: '',
      isFilterActive: false,
      loading: false,
    }
  );

  const shouldShowNoResults = computed(
    () =>
      !props.loading &&
      props.members.length === 0 &&
      (!!props.searchTerm?.trim() || props.isFilterActive)
  );

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    'toggle-all': [];
    edit: [id: number];
    delete: [id: number];
  }>();

  function registrationDisplay(member: AdminMember): string {
    const u = member.user;
    if (!u) return '-';
    const raw = u.registrationNumber?.trim();
    return raw ? raw : '-';
  }

  const handleEdit = (member: AdminMember) => {
    emit('edit', member.id);
  };

</script>

<template>
  <div class="overflow-x-auto">
    <table class="min-w-full">
      <thead
        v-if="!shouldShowNoResults"
        class="bg-white-01 divide-y divide-gray-02"
      >
        <tr>
          <th
            scope="col"
            class="w-20 align-middle text-center"
          >
            <Checkbox
              id="members-select-all"
              :checked="allSelected"
              @toggle="emit('toggle-all')"
            />
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Membros
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Matrícula
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Departamento
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Cargo
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Atividade Funcional
          </th>
          <th
            scope="col"
            class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle"
          >
            Ações
          </th>
        </tr>
      </thead>
      <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
        <tr
          v-for="member in members"
          :key="member.id"
          class="table-row-hover transition-colors duration-300 ease-out"
        >
          <td class="w-20 align-middle text-center">
            <Checkbox
              :id="member.id"
              :checked="isSelected(member.id)"
              @toggle="emit('toggle-selection', member.id)"
            />
          </td>
          <td class="pl-6 py-4.5 whitespace-nowrap text-sm text-gray-07 text-left">
            {{ member.user?.name ?? '-' }}
          </td>
          <td class="pl-6 py-4.5 whitespace-nowrap text-sm text-gray-07 text-left">
            {{ registrationDisplay(member) }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-07">
            <MemberOverflowCell :display="departmentOverflowFromMember(member)" />
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-07">
            <MemberOverflowCell :display="positionNamesOverflowFromMember(member)" />
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-07">
            <MemberOverflowCell :display="functionalActivitiesOverflowFromMember(member)" />
          </td>
          <td class="pl-6 py-4 text-sm text-gray-07 w-50">
            <div class="flex gap-2 text-gray-xx-02 items-center">
              <Tooltip
                content="Editar"
                placement="top"
                class="flex items-center"
              >
                <button
                  type="button"
                  class="cursor-pointer"
                  aria-label="Editar"
                  @click="handleEdit(member)"
                >
                  <IconImage
                    :src="PencilIcon"
                    alt="Editar"
                  />
                </button>
              </Tooltip>
              <Tooltip
                content="Deletar"
                placement="top"
                class="flex items-center"
              >
                <button
                  type="button"
                  class="cursor-pointer"
                  aria-label="Deletar"
                  @click="emit('delete', member.id)"
                >
                  <IconImage
                    :src="TrashIcon"
                    alt="Deletar"
                  />
                </button>
              </Tooltip>
              <InfoTooltip
                :icon="InfoIcon"
                title="Informações"
                placement="left"
                :created-at="formatMemberAuditDateTime(member.createdAt)"
                :created-by="member.creator?.name"
                :updated-at="formatMemberAuditDateTime(member.updatedAt)"
                :updated-by="member.updater?.name"
              >
                <button
                  type="button"
                  class="cursor-default"
                  aria-label="Informações"
                >
                  <IconImage
                    :src="InfoIcon"
                    alt="Informações"
                  />
                </button>
              </InfoTooltip>
            </div>
          </td>
        </tr>
        <tr v-if="shouldShowNoResults">
          <td colspan="100">
            <SearchNoResults />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
